/**
 * Display helpers for People-collection items. Centralised here because the
 * field keys carry spaces (`"first name"`, `"last name"`) and matching their
 * exact casing in every render site is error-prone.
 *
 * The collection schema is locked at `schema_version: 1` (see past_work/collections/PEOPLE_PLAN.md
 * §2). If field names ever shift, change them in one place.
 */

export interface PersonLikeData {
  'first name'?: string | null
  'last name'?: string | null
  nickname?: string | null
  picture?: string | null
  relation?: string | null
  notes?: string | null
  phone?: string | null
  email?: string | null
  'Instagram handle'?: string | null
  website?: string | null
  [key: string]: any
}

const trimOrNull = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

/**
 * Render a Person's display name. Prefers `{first} {last}` when both are set;
 * falls back to first-or-last alone, then nickname, then a "Person" placeholder
 * (which only shows if the row is malformed — `first name` is required by the
 * canonical schema).
 */
export function formatPersonDisplayName(data: PersonLikeData | null | undefined): string {
  if (!data) return 'Person'
  const first = trimOrNull(data['first name'])
  const last = trimOrNull(data['last name'])
  if (first && last) return `${first} ${last}`
  if (first) return first
  if (last) return last
  const nickname = trimOrNull(data.nickname)
  if (nickname) return nickname
  return 'Person'
}

/**
 * Two-letter initials for the avatar fallback (no picture). Uses the first
 * letter of `first name` and `last name`; if only one is present, returns that
 * single letter. Falls back to '?' for malformed rows so the chip still has a
 * tap target.
 */
export function getPersonInitials(data: PersonLikeData | null | undefined): string {
  if (!data) return '?'
  const first = trimOrNull(data['first name'])
  const last = trimOrNull(data['last name'])
  const firstChar = first ? first[0]!.toUpperCase() : ''
  const lastChar = last ? last[0]!.toUpperCase() : ''
  const combined = `${firstChar}${lastChar}`
  if (combined) return combined
  const nickname = trimOrNull(data.nickname)
  if (nickname) return nickname[0]!.toUpperCase()
  return '?'
}

/**
 * Split a typed-in full name into `{ first, last }` on the first run of
 * whitespace, so "Joe Blow" seeds the create form's two name fields instead of
 * landing wholesale in "first name". Anything after that first gap stays
 * together ("Joe Van Blow" → last name "Van Blow"), and a single word leaves
 * `last` empty.
 */
export function splitPersonName(text: string): { first: string; last: string } {
  const normalized = text.trim().replace(/\s+/g, ' ')
  const gap = normalized.indexOf(' ')
  if (gap === -1) return { first: normalized, last: '' }
  return { first: normalized.slice(0, gap), last: normalized.slice(gap + 1) }
}
